import React, { useMemo } from "react";
import { useEarningsTranscriptDates } from "../hooks/useEarningsTranscriptDates";
import { getCurrentQuarter, QuarterOption } from "../utils/quarterUtils";
import { TranscriptMode } from "./TranscriptModeToggle";
import ViewCompanyEarningsTranscript from "../ViewCompanyEarningsTranscript";

interface TranscriptState {
  year: number;
  quarter: number;
  mode: TranscriptMode;
}

export interface EarningsTranscriptTabState {
  year: number;
  quarter: number;
  mode: TranscriptMode;
  quarterOptions: QuarterOption[];
  selectedQuarter: string;
  isLoadingDates: boolean;
  onStateChange: (updates: Partial<TranscriptState>) => void;
  onQuarterSelect: (option: QuarterOption) => void;
}

interface EarningsTranscriptTabProps {
  ticker: string;
  year: number;
  quarter: number;
  mode: TranscriptMode;
  quarterOptions: QuarterOption[];
  selectedQuarter: string;
  isLoadingDates: boolean;
  onStateChange: (updates: Partial<TranscriptState>) => void;
  onQuarterSelect: (option: QuarterOption) => void;
}

const EarningsTranscriptTab: React.FC<EarningsTranscriptTabProps> = ({
  ticker,
  year,
  quarter,
  mode,
  quarterOptions,
  selectedQuarter,
  isLoadingDates,
  onStateChange,
  onQuarterSelect,
}) => {
  return (
    <ViewCompanyEarningsTranscript
      symbol={ticker}
      year={year}
      quarter={quarter}
      mode={mode}
      onStateChange={onStateChange}
      quarterOptions={quarterOptions}
      selectedQuarter={selectedQuarter}
      onQuarterSelect={onQuarterSelect}
      isLoadingDates={isLoadingDates}
      fillHeight={false}
    />
  );
};

export const useEarningsTranscriptTabState = (ticker: string, enabled = true): EarningsTranscriptTabState => {
  const defaultQuarter = getCurrentQuarter();

  const [state, setState] = React.useState<TranscriptState>({
    year: defaultQuarter.year,
    quarter: defaultQuarter.quarter,
    mode: "ai-summary",
  });

  const { data: transcriptDatesData, isLoading: isLoadingDates } =
    useEarningsTranscriptDates(enabled ? ticker : undefined);

  const quarterOptions = useMemo((): QuarterOption[] => {
    if (!transcriptDatesData) return [];

    return transcriptDatesData.map((item) => ({
      year: parseInt(item.value.split("-")[0]),
      quarter: parseInt(item.value.split("-")[1]),
      label: item.label,
      value: item.value,
      isCurrentQuarter: item.isCurrentQuarter,
    }));
  }, [transcriptDatesData]);

  const selectedQuarter = `${state.year}-${state.quarter}`;

  const onStateChange = (updates: Partial<TranscriptState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const onQuarterSelect = (quarterOption: QuarterOption) => {
    setState((prev) => ({
      ...prev,
      year: quarterOption.year,
      quarter: quarterOption.quarter,
    }));
  };

  return {
    year: state.year,
    quarter: state.quarter,
    mode: state.mode,
    quarterOptions,
    selectedQuarter,
    isLoadingDates,
    onStateChange,
    onQuarterSelect,
  };
};

export default EarningsTranscriptTab;
